import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Users, Package, AlertTriangle, CalendarDays, Wrench,
  ChevronRight, MapPin, LogOut, Droplets
} from 'lucide-react'
import { dashboard } from '../lib/api'
import { useAuthStore } from '../store/authStore'
import { Card } from '../components/ui/Card'
import { besuchStatusBadge } from '../components/ui/Badge'
import { formatDatum } from '../lib/utils'
import { LoadingSpinner } from '../components/ui/EmptyState'
import { BESUCH_TYP_LABEL } from '../lib/utils'

export function DashboardPage() {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboard.get(),
    refetchInterval: 60_000,
  })

  const stats = [
    { label: 'Kunden', value: data?.kunden ?? 0, icon: Users, color: 'bg-blue-50 text-blue-600', to: '/kunden' },
    { label: 'Geräte aktiv', value: data?.geraete_aktiv ?? 0, icon: Package, color: 'bg-emerald-50 text-emerald-600', to: '/inventar' },
    { label: 'Defekte offen', value: data?.defekte_offen ?? 0, icon: AlertTriangle, color: 'bg-red-50 text-red-600', to: '/defekte' },
    { label: 'Heute Besuche', value: data?.besuche_heute ?? 0, icon: CalendarDays, color: 'bg-amber-50 text-amber-600', to: '/besuche' },
  ]

  return (
    <div className="min-h-dvh bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-900 to-blue-700 px-5 pt-safe-top pb-8">
        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
              <Droplets size={18} className="text-white" />
            </div>
            <div>
              <p className="text-blue-200 text-xs">Willkommen zurück</p>
              <p className="text-white font-semibold text-sm">{user?.name}</p>
            </div>
          </div>
          <button
            onClick={() => { logout(); navigate('/login') }}
            className="p-2 rounded-xl bg-white/10 text-white/80 hover:bg-white/20 transition-colors"
          >
            <LogOut size={18} />
          </button>
        </div>
        <div className="mt-6">
          <h2 className="text-white text-xl font-bold">Übersicht</h2>
          <p className="text-blue-200 text-sm">{new Date().toLocaleDateString('de-CH', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
        </div>
      </div>

      <div className="px-4 -mt-4 space-y-4 pb-6">
        {/* Stats Grid */}
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {stats.map((s) => (
              <Card
                key={s.label}
                clickable
                onClick={() => navigate(s.to)}
                className="flex flex-col gap-3"
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.color}`}>
                  <s.icon size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Nächste Besuche */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Nächste Besuche</h3>
            <button
              onClick={() => navigate('/besuche')}
              className="text-sm text-blue-600 flex items-center gap-0.5"
            >
              Alle <ChevronRight size={16} />
            </button>
          </div>
          {data?.naechste_besuche && data.naechste_besuche.length > 0 ? (
            <div className="space-y-2">
              {data.naechste_besuche.map((besuch) => (
                <Card
                  key={besuch.id}
                  clickable
                  onClick={() => navigate(`/besuche/${besuch.id}`)}
                  className="flex items-center gap-3"
                >
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex flex-col items-center justify-center shrink-0">
                    <span className="text-blue-700 font-bold text-sm leading-none">
                      {formatDatum(besuch.datum, 'dd')}
                    </span>
                    <span className="text-blue-500 text-xs">
                      {formatDatum(besuch.datum, 'MMM')}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{besuch.kunde_name}</p>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <MapPin size={10} />
                      <span className="truncate">{besuch.kunde_ort}</span>
                      <span>· {BESUCH_TYP_LABEL[besuch.typ] ?? besuch.typ}</span>
                    </div>
                  </div>
                  <div className="shrink-0">{besuchStatusBadge(besuch.status)}</div>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <div className="flex items-center gap-3 py-2">
                <CalendarDays size={20} className="text-gray-400" />
                <p className="text-sm text-gray-500">Keine geplanten Besuche</p>
              </div>
            </Card>
          )}
        </div>

        {/* Quick Actions */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-3">Schnellzugriff</h3>
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Neuer Besuch', icon: CalendarDays, to: '/besuche/neu', color: 'text-blue-600 bg-blue-50' },
              { label: 'Defekt melden', icon: AlertTriangle, to: '/defekte/neu', color: 'text-red-600 bg-red-50' },
              { label: 'Service-FAQ', icon: Wrench, to: '/faq', color: 'text-amber-600 bg-amber-50' },
            ].map((a) => (
              <button
                key={a.label}
                onClick={() => navigate(a.to)}
                className="bg-white rounded-2xl border border-gray-100 p-4 flex flex-col items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all"
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${a.color}`}>
                  <a.icon size={20} />
                </div>
                <span className="text-xs font-medium text-gray-700 text-center leading-tight">{a.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
